import React from 'react';
import { Box, Typography, TextField, Button, Paper, Checkbox, FormControlLabel, FormControl, InputLabel, Select, MenuItem, IconButton } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useLocation, useNavigate } from 'react-router-dom';
import ConfirmModal from '../../components/common/ConfirmModal';
import { PATHS } from '../../routes/paths';
import {
  createShopApi,
  updateShopApi,
  getShopDetailApi,
  getServerBaseUrl,
  createShopPhotoApi,
  getShopCategoriesApi,
} from '../../api';
import ShopCategoryEditModal from '../../components/Shops/ShopCategoryEditModal';

const ShopRegister = () => {
  const [currentUser, setCurrentUser] = React.useState(null);
  const [authOpen, setAuthOpen] = React.useState(false);
  const [successOpen, setSuccessOpen] = React.useState(false);

  const [name, setName] = React.useState('');
  const [category, setCategory] = React.useState('');
  const [businessHours, setBusinessHours] = React.useState('');
  const [location, setLocation] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [isFeatured, setIsFeatured] = React.useState(false);
  const [photos, setPhotos] = React.useState([null, null, null, null, null, null]);
  const [activePhotoIndex, setActivePhotoIndex] = React.useState(null);
  const [editingShopId, setEditingShopId] = React.useState(null);
  const [categoryOptions, setCategoryOptions] = React.useState([]);
  const [categoryModalOpen, setCategoryModalOpen] = React.useState(false);

  const navigate = useNavigate();
  const routeLocation = useLocation();
  const fileInputRef = React.useRef(null);
  const serverUrl = React.useMemo(() => getServerBaseUrl(), []);
  const [errors, setErrors] = React.useState({
    name: '',
    category: '',
    businessHours: '',
    location: '',
    description: '',
    photos: '',
  });

  React.useEffect(() => {
    try {
      const raw = window.localStorage.getItem('currentUser');
      if (raw) {
        const parsed = JSON.parse(raw);
        setCurrentUser(parsed);
        if (parsed.role !== 'ADMIN') {
          setAuthOpen(true);
        }
      } else {
        setCurrentUser(null);
        setAuthOpen(true);
      }
    } catch {
      setCurrentUser(null);
      setAuthOpen(true);
    }
  }, []);

  const isAdmin = currentUser?.role === 'ADMIN';

  const loadCategories = React.useCallback(async () => {
    try {
      const res = await getShopCategoriesApi();
      const list = Array.isArray(res?.data) ? res.data : res;
      const normalized = (list || []).map((item) => ({
        id: item.categoryId ?? item.category_id ?? item.id,
        value: item.categoryId ?? item.category_id ?? item.id,
        label: item.name,
        sortOrder: item.sortOrder ?? item.sort_order ?? 0,
      }));
      normalized.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      setCategoryOptions(normalized);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to load shop categories:', err);
    }
  }, []);

  React.useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  React.useEffect(() => {
    if (!category || categoryOptions.length === 0) return;
    const hasNumericId = categoryOptions.some((opt) => String(opt.value) === String(category));
    if (hasNumericId) return;
    const matched = categoryOptions.find((opt) => opt.label === category);
    if (matched) {
      setCategory(matched.value);
      return;
    }
    setCategory('');
  }, [category, categoryOptions]);

  React.useEffect(() => {
    const state = routeLocation.state;
    if (!state || state.mode !== 'edit' || !state.shop) return;

    const shop = state.shop;
    setEditingShopId(shop.shopId);
    setName(shop.name || '');
    if (shop.categoryId || shop.category_id) {
      setCategory(shop.categoryId ?? shop.category_id ?? '');
    }

    setBusinessHours(shop.openingHours || '');
    setLocation(shop.locationText || '');
    setDescription(shop.description || '');
    setIsFeatured(Boolean(shop.isPartner));
  }, [routeLocation.state]);

  React.useEffect(() => {
    if (!editingShopId) return;

    const toFullUrl = (p) => {
      if (!p) return null;
      if (p.startsWith('http://') || p.startsWith('https://')) return p;
      return `${serverUrl}${p}`;
    };

    async function loadShopPhotos() {
      try {
        const res = await getShopDetailApi(editingShopId);
        const shop = res?.data || null;
        const list = Array.isArray(shop?.photos) ? shop.photos : [];

        const initial = [null, null, null, null, null, null];
        list.slice(0, 6).forEach((p, idx) => {
          const url = p?.fileUrl || p?.url || p?.path;
          if (!url) return;
          initial[idx] = {
            existingUrl: url,
            preview: toFullUrl(url),
          };
        });

        setPhotos(initial);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
      }
    }

    loadShopPhotos();
  }, [editingShopId, serverUrl]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isAdmin) return;

    const nextErrors = {
      name: '',
      category: '',
      businessHours: '',
      location: '',
      description: '',
      photos: '',
    };

    if (!name.trim()) nextErrors.name = '필수 입력사항입니다.';
    if (!category) nextErrors.category = '필수 입력사항입니다.';
    if (!businessHours.trim()) nextErrors.businessHours = '필수 입력사항입니다.';
    if (!location.trim()) nextErrors.location = '필수 입력사항입니다.';

    const plainDesc = String(description || '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (!plainDesc) nextErrors.description = '필수 입력사항입니다.';

    const hasAtLeastOnePhoto = photos.some((p) => p?.file || p?.existingUrl);
    if (!hasAtLeastOnePhoto) {
      nextErrors.photos = '최소 1장 이상의 이미지를 등록해주세요.';
    }

    const hasErrors = Object.values(nextErrors).some(Boolean);
    if (hasErrors) {
      setErrors(nextErrors);
      return;
    }

    try {
      if (editingShopId) {
        const newPhotoPayloads = photos
          .map((p, idx) => (p?.file ? { file: p.file, sortOrder: idx + 1 } : null))
          .filter(Boolean);

        await updateShopApi(editingShopId, {
          name: name.trim(),
          categoryId: category || undefined,
          openingHours: businessHours.trim(),
          locationText: location.trim(),
          description,
          isPartner: isFeatured ? 1 : 0,
        });

        if (newPhotoPayloads.length > 0) {
          await Promise.all(
            newPhotoPayloads.map(({ file, sortOrder }) =>
              createShopPhotoApi({
                shopId: editingShopId,
                sortOrder,
                file,
              })
            )
          );
        }
      } else {
        const formData = new FormData();

        formData.append('name', name.trim());
        if (category) {
          formData.append('categoryId', category);
        }
        formData.append('openingHours', businessHours.trim());
        formData.append('locationText', location.trim());
        formData.append('description', description);
        formData.append('isPartner', isFeatured ? '1' : '0');

        photos.forEach((p) => {
          if (p?.file) {
            formData.append('photos', p.file);
          }
        });

        await createShopApi(formData);
      }

      setSuccessOpen(true);
    } catch (err) {
      if (err?.status === 401 || err?.suppressAlert) return;
      // eslint-disable-next-line no-alert
      window.alert(err.message || '업체 등록 중 오류가 발생했습니다.');
    }
  };

  const handleSubmitCategories = (nextCategories) => {
    setCategoryOptions(nextCategories);
    const stillExists = nextCategories.some((opt) => opt.value === category);
    if (!stillExists) {
      setCategory('');
    }
    setCategoryModalOpen(false);
  };

  const handleClickPhotoSlot = (index) => {
    setActivePhotoIndex(index);
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleChangePhoto = (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setPhotos((prev) => {
      const next = [...prev];
      let startIndex = activePhotoIndex ?? 0;

      for (let i = 0; i < files.length; i += 1) {
        const file = files[i];
        let targetIndex = startIndex;

        while (targetIndex < next.length && next[targetIndex]) {
          targetIndex += 1;
        }

        if (targetIndex >= next.length) break;

        next[targetIndex] = {
          file,
          preview: URL.createObjectURL(file),
        };
        startIndex = targetIndex + 1;
      }

      return next;
    });

    if (files.length && errors.photos) {
      setErrors((prev) => ({ ...prev, photos: '' }));
    }

    // eslint-disable-next-line no-param-reassign
    event.target.value = '';
  };

  const handleRemovePhoto = (index) => {
    setPhotos((prev) => {
      const next = [...prev];
      next[index] = null;
      return next;
    });
  };

  return (
    <Box>
      <Box
        sx={{
          maxWidth: 430,
          mx: 'auto',
          bgcolor: 'white',
          minHeight: '100vh',
          borderRadius: 0,
          boxShadow: '0 0 20px rgba(0,0,0,0.06)',
          px: 2,
          position: 'relative',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: 60,
            borderBottom: '1px solid #e0e0e0',
            mb: 2,
            mx: -2,
            px: 2,
            position: 'relative',
          }}
        >
          <IconButton
            onClick={() => navigate(-1)}
            sx={{ p: 0, position: 'absolute', left: 16 }}
          >
            <ArrowBackIosNewIcon sx={{ fontSize: 20, color: '#333' }} />
          </IconButton>
          <Typography
            sx={{
              flex: 1,
              textAlign: 'center',
              fontSize: 18,
              fontWeight: 700,
            }}
          >
            {editingShopId ? '업체 수정' : '업체 등록'}
          </Typography>
        </Box>

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            mt: 0.5,
            mb: 4,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              borderRadius: 1,
              p: 0,
            }}
          >
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    minWidth: 48,
                  }}
                >
                  업체명
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={name}
                  error={Boolean(errors.name)}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (errors.name) setErrors((p) => ({ ...p, name: '' }));
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: 14,
                      '& input': { py: 1.2 },
                    },
                  }}
                />
              </Box>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    minWidth: 48,
                  }}
                >
                  업종
                </Typography>
                <FormControl
                  fullWidth
                  size="small"
                  error={Boolean(errors.category)}
                  sx={{
                    flex: 1,
                    '& .MuiInputLabel-root': {
                      fontSize: 13,
                    },
                  }}
                >
                  <InputLabel id="shop-category-label">업종 선택</InputLabel>
                  <Select
                    labelId="shop-category-label"
                    value={category}
                    label="업종 선택"
                    onChange={(e) => {
                      setCategory(e.target.value);
                      if (errors.category) setErrors((p) => ({ ...p, category: '' }));
                    }}
                    MenuProps={{
                      disableScrollLock: true,
                    }}
                    sx={{
                      '& .MuiSelect-select': {
                        py: 1.1,
                        fontSize: 13,
                      },
                      bgcolor: '#fff',
                    }}
                  >
                    {(categoryOptions || []).map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {isAdmin && (
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{
                      minWidth: 0,
                      px: 1.5,
                      height: 40,
                      borderRadius: 1,
                      fontSize: 13,
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                      borderColor: '#d0d0d0',
                      color: '#000',
                      '&:hover': {
                        borderColor: '#b0b0b0',
                        bgcolor: '#f8f8f8',
                      },
                    }}
                    onClick={() => setCategoryModalOpen(true)}
                  >
                    추가
                  </Button>
                )}
              </Box>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    minWidth: 48,
                  }}
                >
                  영업시간
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={businessHours}
                  error={Boolean(errors.businessHours)}
                  onChange={(e) => {
                    setBusinessHours(e.target.value);
                    if (errors.businessHours) setErrors((p) => ({ ...p, businessHours: '' }));
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: 14,
                      '& input': { py: 1.2 },
                    },
                  }}
                />
              </Box>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    minWidth: 48,
                  }}
                >
                  위치
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={location}
                  error={Boolean(errors.location)}
                  onChange={(e) => {
                    setLocation(e.target.value);
                    if (errors.location) setErrors((p) => ({ ...p, location: '' }));
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: 14,
                      '& input': { py: 1.2 },
                    },
                  }}
                />
              </Box>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, mb: 1, fontSize: 13 }}
              >
                내용
              </Typography>
              <Box
                sx={{
                  '& .quill': {
                    width: '100%',
                    marginRight: 0,
                  },
                  ...(errors.description
                    ? {
                        '& .ql-toolbar.ql-snow': { borderColor: '#d32f2f' },
                        '& .ql-container.ql-snow': { borderColor: '#d32f2f' },
                      }
                    : {}),
                  '& .ql-toolbar.ql-snow .ql-formats': {
                    marginRight: '0px',
                  },
                  '& .ql-toolbar.ql-snow .ql-formats:last-of-type': {
                    marginRight: 0,
                  },
                  '& .ql-container': {
                    minHeight: 200,
                    fontSize: 14,
                  },
                }}
              >
                <ReactQuill
                  theme="snow"
                  value={description}
                  onChange={(v) => {
                    setDescription(v);
                    if (errors.description) setErrors((p) => ({ ...p, description: '' }));
                  }}
                  placeholder="내용을 작성해주세요."
                />
              </Box>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  mb: 0.5,
                  fontSize: 13,
                  color: errors.photos ? '#d32f2f' : 'inherit',
                }}
              >
                사진
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {[0, 1, 2, 3, 4, 5].map((idx) => (
                  <Paper
                    key={idx}
                    elevation={0}
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: 1,
                      border: '1px solid #d0d0d0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      overflow: 'hidden',
                      position: 'relative',
                    }}
                    onClick={() => handleClickPhotoSlot(idx)}
                  >
                    {photos[idx]?.preview ? (
                      <>
                        <Box
                          component="img"
                          src={photos[idx].preview}
                          alt={`업체 사진 ${idx + 1}`}
                          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            width: 16,
                            height: 16,
                            bgcolor: 'rgba(0,0,0,0.5)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemovePhoto(idx);
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: '#fff', fontSize: 10, lineHeight: 1 }}
                          >
                            ×
                          </Typography>
                        </Box>
                      </>
                    ) : (
                      <Typography variant="h6" sx={{ lineHeight: 1 }}>
                        +
                      </Typography>
                    )}
                  </Paper>
                ))}
              </Box>

              {errors.photos && (
                <Typography
                  variant="caption"
                  sx={{ color: '#d32f2f', display: 'block', mt: 0.8 }}
                >
                  {errors.photos}
                </Typography>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                style={{ display: 'none' }}
                onChange={handleChangePhoto}
              />

              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <Checkbox
                    size="small"
                    checked={isFeatured}
                    onChange={(e) => setIsFeatured(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="caption">
                    제휴 업체로 설정 (상단 고정 노출)
                  </Typography>
                }
              />
            </Box>
          </Paper>

          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={{
              mt: 0.5,
              bgcolor: '#0d47a1',
              py: 1.2,
              '&:hover': { bgcolor: '#0b3a81' },
              fontWeight: 600,
            }}
            disabled={!isAdmin}
          >
            저장
          </Button>
        </Box>
      </Box>

      <ConfirmModal
        open={authOpen && !isAdmin}
        message="관리자만 접근 가능합니다."
        onConfirm={() => {
          setAuthOpen(false);
          navigate(PATHS.root);
        }}
      />

      <ConfirmModal
        open={successOpen}
        message={editingShopId ? '업체 정보가 수정되었습니다.' : '업체 등록이 완료되었습니다.'}
        onConfirm={() => {
          setSuccessOpen(false);
          if (editingShopId) {
            navigate(PATHS.adminShops);
          } else {
            navigate(PATHS.shops);
          }
        }}
      />

      {isAdmin && (
        <ShopCategoryEditModal
          open={categoryModalOpen}
          onSubmit={handleSubmitCategories}
          onClose={() => setCategoryModalOpen(false)}
        />
      )}
    </Box>
  );
};

export default ShopRegister;


